import { useMemo } from "react";

import { supportsThempus } from "@punto/frontend-shared/constants/procedure-types";
import { useTranslation } from "@punto/frontend-shared/i18n/i18next";

import { ProcedureStatuses, ProcedureTypes } from "@/modules/procedures/types";

const procedureStatuses = [
  ProcedureStatuses.DRAFT,
  ProcedureStatuses.PENDING,
  ProcedureStatuses.IN_PROGRESS,
  ProcedureStatuses.BLOCKED,
  ProcedureStatuses.ON_HOLD,
  ProcedureStatuses.DELIVERED_TO_DGT,
  ProcedureStatuses.RECEIVED,
  ProcedureStatuses.DELIVERED_TO_CLIENT,
  ProcedureStatuses.CANCELLED,
] as ProcedureStatuses[];

/**
 * P-2464: `DELIVERED_TO_DGT` is written by the DGT-result saga for procedure
 * types with a Thempus step, so a gestor may pick it manually only for types
 * without one. Derived from `procedureStatuses` rather than duplicated, so the
 * option keeps its canonical position (after ON_HOLD, before RECEIVED).
 */
const procedureStatusesWithoutDgt = procedureStatuses.filter(
  (status) => status !== ProcedureStatuses.DELIVERED_TO_DGT,
);

export const useProcedureStatuses = (procedureType?: ProcedureTypes) => {
  const { t } = useTranslation();

  const statusObject = t("Procedures.by_status", {
    returnObjects: true,
  }) as Record<string, string>;

  const options = useMemo(
    () =>
      procedureStatuses.map((status) => ({
        value: status,
        label: statusObject[status] ?? status,
      })),
    [statusObject],
  );

  // Fail closed: a missing type keeps the pre-P-2464 behaviour.
  const canPickDeliveredToDgt =
    !!procedureType && !supportsThempus(procedureType);

  return {
    options,
    objectByKey: statusObject,
    literalArray: procedureStatuses,
    statusChangeArray: canPickDeliveredToDgt
      ? procedureStatuses
      : procedureStatusesWithoutDgt,
  };
};
